import './container-page.scss'

import * as _ from 'lodash'
import { identity } from 'lodash'
import { Observable, of } from 'rxjs'
import {
    catchError,
    delay,
    first,
    map,
    retryWhen,
    switchMap,
    take,
    tap,
} from 'rxjs/operators'
import { defineComponent } from 'vue'
import {
    faExclamationTriangle,
    faCheck,
    faTimes,
    faCircleNotch,
} from '@fortawesome/free-solid-svg-icons'

import { Container, Item, ItemTypeWrapper, SearchResponse, SearchResult, SpecimenResource, Wemi, isSearchResult } from '@skinsoft/types'
import { TrackingRoutes } from '@skinsoft/const'
import { API_ENDPOINTS } from '@skinsoft/network'
import { itemSelectors, specimenSelectors } from '@skinsoft/selectors'
import { searchResultSelectors } from '@skinsoft/search-data-store'
import { genericRetryStrategy } from '@skinsoft/rxjs-utils/generic-retry-strategy'

import { reactiveVueMixin } from '@skinsoft/reactive-vue'
import { ContainerFeaturedCard } from '@skinsoft/entity-ui/src/container'
import { AddAccessoryModal, AddContainerModal, ItemCard, SpecimenCard, Touchable } from '@skinsoft/ui'
import { ContainerItemList } from './container-item-list'
import { getContentTypeFromTypeKey, ListContentType } from './list-content-type'
import { DELETE_CARD } from '@skinsoft/events'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus/src/busTopics'
import { CANCEL_ALL_SELECT_ITEM, CANCEL_ALL_SELECT_SPECIMEN, CANCEL_ONE_SELECT_ITEM, CANCEL_ONE_SELECT_SPECIMEN, RETRY_ALL_SELECT_ITEM, RETRY_ALL_SELECT_SPECIMEN, RETRY_ONE_SELECT_ITEM, RETRY_ONE_SELECT_SPECIMEN } from '@skinsoft/ui2'
import { ItemSelectionFailure, SelectItemsErrorDialog, SpecimenSelectionFailure, SelectSpecimensErrorDialog } from '@skinsoft/ui2'

import {
    ADD_ACCESSORY_TOPIC,
    ADD_CONTAINER_TOPIC,
    ADD_ITEM_TOPIC,
    ADD_LOCATION_TOPIC,
    ADD_SPECIMEN_TOPIC,
    CLOSE_CONTAINER_TOPIC,
    DELETE_CONTAINER_TOPIC,
    OPEN_CONTAINER_TOPIC,
} from '../../../busTopics'
import { SELECT_ITEM_MULTIPLE_MODAL_MULTIPLE_ITEMS_SELECTED } from '../../../events'
import { SIMMItemSelected, SelectItemMultipleModal } from '../../component/select-item-multiple-modal'
import { ChooseItemModal } from '../../component/choose-item-modal'

declare const config
const ITEM_SEARCH_TYPES: string[] = config.item_search_types

const MAX_NB_CHAIN_ADD = 50

// we define the interface here and not in type as it is only meant to be used inside this file
interface ExpectedChange {
    operationType: 'update' | 'delete' | 'create'
    expectedValue?: {
        // we expect item with id idItem to have a property corresponding to the path with the adequate value
        // if it is the case, we know the update was successful
        idItem: string
        path: string
        value: any
    }
    contentType?: 'item' | 'specimen'
}

export const ContainerPage = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sItemCard: ItemCard,
        sSpecimenCard: SpecimenCard,
        sContainerFeaturedCard: ContainerFeaturedCard,
        sSelectItemMultipleModal: SelectItemMultipleModal,
        sTouchable: Touchable,
        sAddAccessoryModal: AddAccessoryModal,
        sAddContainerModal: AddContainerModal,
        sContainerItemList: ContainerItemList,
        sChooseItemModal: ChooseItemModal,
        sSelectItemsErrorDialog: SelectItemsErrorDialog,
        sSelectSpecimensErrorDialog: SelectSpecimensErrorDialog,
    },
    template: require('./container-page.html'),
    data() {
        return {
            exclamationTriangleIcon: faExclamationTriangle,
            checkIcon: faCheck,
            crossIcon: faTimes,
            spinnerIcon: faCircleNotch,
            ListContentType,
            itemTypes: ITEM_SEARCH_TYPES,
            specimenTypes: ['specimen'],
            containerTypes: ['physicalContainerItem', 'numericContainerItem'],
            accessoryTypes: ['physicalAccessoryItem', 'numericAccessoryItem'],
            container: null as any as Container,
            items: [] as unknown as ItemTypeWrapper[],
            specimens: [] as unknown as ItemTypeWrapper[],
            subContainers: [] as ItemTypeWrapper[],
            accessories: [] as ItemTypeWrapper[],
            currentlyAddingTo: null as ListContentType | null,
            failedItemsSelections: [] as ItemSelectionFailure[],
            failedSpecimensSelections: [] as SpecimenSelectionFailure[],
            MAX_NB_CHAIN_ADD,
            DELETE_CARD,
            SELECT_ITEM_MULTIPLE_MODAL_MULTIPLE_ITEMS_SELECTED,
            CANCEL_ALL_SELECT_ITEM,
            CANCEL_ONE_SELECT_ITEM,
            RETRY_ALL_SELECT_ITEM,
            RETRY_ONE_SELECT_ITEM,
            CANCEL_ALL_SELECT_SPECIMEN,
            CANCEL_ONE_SELECT_SPECIMEN,
            RETRY_ALL_SELECT_SPECIMEN,
            RETRY_ONE_SELECT_SPECIMEN,
        }
    },
    mounted() {
        this.subscribeToBus(ADD_ITEM_TOPIC, this.openAddItemModal)
        this.subscribeToBus(ADD_SPECIMEN_TOPIC, () => this.openAddSpecimenModal())
        this.subscribeToBus(ADD_ACCESSORY_TOPIC, () => this.openAddAccessoryModal())
        this.subscribeToBus(CLOSE_CONTAINER_TOPIC, () => this.toggleContainerOpen(false))
        this.subscribeToBus(OPEN_CONTAINER_TOPIC, () => this.toggleContainerOpen())
        this.subscribeToBus(ADD_CONTAINER_TOPIC, this.openAddContainerModal)
        this.subscribeToBus(ADD_LOCATION_TOPIC, this.openLocationModal)
        this.subscribeToBus(DELETE_CONTAINER_TOPIC, this.deleteContainer)
        this.subscribeToEntity()
    },
    methods: {
        subscribeToEntity(checkUpdated?: ExpectedChange) {
            this.subscribeTo(
                (this as any).$containerDataStore.fetchOne(this.$route.params.id),
                (container) => {
                    this.container = container
                    
                    if (!checkUpdated?.contentType || checkUpdated.contentType === 'item') { this.subscribeToItems(checkUpdated) }
                    if (!checkUpdated?.contentType || checkUpdated.contentType === 'specimen') { this.subscribeToSpecimens(checkUpdated) }
                },
                'container',
            )
        },
        async subscribeToItems(expectedChange?: ExpectedChange) {
            const observable$ = of(this.container.id).pipe(
                switchMap((id) =>
                    this.$containerDataStore.getAssociatedItems(
                        id,
                        ITEM_SEARCH_TYPES.concat(this.containerTypes).concat(this.accessoryTypes),
                    ),
                ),
                // When we fetch after a modification on the data, sometimes the result we get from
                // this.$containerDataStore.getAssociatedItems() are not up to date yet
                tap((items) => {
                    if(expectedChange) {
                        switch(expectedChange.operationType) {
                            case 'update':
                                if(!(expectedChange.expectedValue && expectedChange.expectedValue.idItem && expectedChange.expectedValue.path && expectedChange.expectedValue.value)) {
                                    // Logger missing param
                                    return // we ignore the check and continue
                                }
                                const itemToCheck = items.find(item => (item as any).id === expectedChange.expectedValue!.idItem)

                                if(itemToCheck) {
                                    const newValue = _.get(itemToCheck, expectedChange.expectedValue.path)
                                    if(newValue === expectedChange.expectedValue.value) {
                                        // OK, we have the updated version
                                        return
                                    }
                                }

                                throw new Error('The expected changes are not available yet')

                            case 'delete':
                            case 'create':
                                const totalEntities = this.items
                                    .concat(this.subContainers)
                                    .concat(this.accessories)

                                if(items.length === totalEntities.length) {
                                    throw new Error('The expected changes are not available yet')
                                }
                                break
                        }
                    }
                }),
                retryWhen(genericRetryStrategy({
                    maxRetryAttempts: 5,
                    scalingDuration: [1000],
                    excludedStatusCodes: []
                })),
                tap(items => {
                    this.subContainers = items.filter((item) => {
                        const type = (item as any).entityType ?? (item as Item).type
                        return type?.includes('Container')
                    })
                    .map(container => {
                        return {
                            item: container,
                            wrapperType: 'SearchResult',
                            itemType: 'Container',
                        } as ItemTypeWrapper
                    })
                }),
                map(items => {
                    return items.filter(item => {
                        const type = (item as any).entityType ?? (item as Item).type
                        return !type?.includes('Container')
                    })
                }),
                tap(items => {
                    this.accessories = items.filter((item) => {
                        const type = (item as any).entityType ?? (item as Item).type
                        return type?.includes('Accessory')
                    })
                    .map(accessory => {
                        return {
                            item: accessory,
                            wrapperType: 'SearchResult',
                            itemType: 'Accessory',
                        } as ItemTypeWrapper
                    })
                }),
                map((items) => {
                    return items.filter(item => {
                        const type = (item as any).entityType ?? (item as Item).type
                        return !type?.includes('Accessory')
                    })
                }),
                tap(() => this.currentlyAddingTo = null)
            )

            this.subscribeTo(
                observable$,
                (items) => {
                    this.items = items.map(item => {
                        return {
                            item: item,
                            wrapperType: 'SearchResult',
                            itemType: 'Item',
                        } as ItemTypeWrapper
                    })
                },
                'items',
            )
        },
        async subscribeToSpecimens(expectedChange?: ExpectedChange) {
            const observable$ = of(this.container.id).pipe(
                switchMap((id) =>
                    this.$containerDataStore.getAssociatedSpecimens(id),
                ),
                // When we fetch after a modification on the data, sometimes the result we get from
                // this.$containerDataStore.getAssociatedItems() are not up to date yet
                tap((specimens) => {
                    if(expectedChange) {
                        switch(expectedChange.operationType) {
                            case 'update':
                                if(!(expectedChange.expectedValue && expectedChange.expectedValue.idItem && expectedChange.expectedValue.path && expectedChange.expectedValue.value)) {
                                    // Logger missing param
                                    return // we ignore the check and continue
                                }
                                const specimenToCheck = specimens.find(specimen => (specimen as any).id === expectedChange.expectedValue!.idItem)

                                if(specimenToCheck) {
                                    const newValue = _.get(specimenToCheck, expectedChange.expectedValue.path)
                                    if(newValue === expectedChange.expectedValue.value) {
                                        // OK, we have the updated version
                                        return
                                    }
                                }

                                throw new Error('The expected changes are not available yet')

                            case 'delete':
                            case 'create':
                                if(specimens.length === this.specimens.length) {
                                    throw new Error('The expected changes are not available yet')
                                }
                                break
                        }
                    }
                }),
                retryWhen(genericRetryStrategy({
                    maxRetryAttempts: 5,
                    scalingDuration: [1000],
                    excludedStatusCodes: []
                })),
                tap(() => this.currentlyAddingTo = null)
            )

            this.subscribeTo(
                observable$,
                (specimens) => {
                    this.specimens = specimens.map(specimen => {
                        return {
                            item: specimen,
                            wrapperType: 'Specimen',
                            itemType: 'Specimen',
                        } as ItemTypeWrapper
                    })
                },
                'specimens',
            )
        },
        async onAddedItemSelected({ id, type, item }: SIMMItemSelected) {
            if (item) {
                this.currentlyAddingTo = getContentTypeFromTypeKey(type)
                //const item = await this.$itemDataStore.fetchOne(id).pipe(first()).toPromise()

                this.addItemsToContainer(
                    [item],
                    undefined,
                    err => {
                        const inventoryNumber = itemSelectors.inventoryNumber(item)
                        let alertMessage = this.$t("container.alerts.errors.addItemFailure", [inventoryNumber])

                        if(item.type) {
                            if(item.type.includes("Container")) {
                                alertMessage = this.$t("container.alerts.errors.addSubContainerFailure", [inventoryNumber])
                            } else if(item.type.includes("Accessory")) {
                                alertMessage = this.$t("container.alerts.errors.addAccessoryFailure", [inventoryNumber])
                            }
                        }

                        const alertPayload = {
                            message: alertMessage,
                            variant: "danger"
                        }

                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        this.currentlyAddingTo = null
                    }
                )
            } else {
                const alertPayload = {
                    message: this.$t("container.alerts.errors.MissingItem"),
                    variant: "danger"
                }

                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                this.currentlyAddingTo = null
            }
        },
        async onAddedSpecimenSelected({ id, type, specimen }: SIMMItemSelected) {
            if (specimen) {
                this.currentlyAddingTo = ListContentType.SPECIMEN

                this.addSpecimensToContainer(
                    [specimen],
                    undefined,
                    () => {
                        const firstIdentifier = specimenSelectors.firstIdentifier(specimen)
                        let alertMessage = this.$t("container.alerts.errors.addItemFailure", [firstIdentifier])

                        const alertPayload = {
                            message: alertMessage,
                            variant: "danger"
                        }

                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        this.currentlyAddingTo = null
                        this.failedSpecimensSelections.push({specimen: specimen})
                        ;(this.$refs.selectSpecimensErrorDialog as InstanceType<typeof SelectSpecimensErrorDialog>).openModal()
                    }
                )
            } else {
                const alertPayload = {
                    message: this.$t("container.alerts.errors.MissingSpecimen"),
                    variant: "danger"
                }

                this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                this.currentlyAddingTo = null
            }
        },
        async onMultipleItemsSelected(selectedItems: SIMMItemSelected[]) {
            if(selectedItems?.length) {
                this.currentlyAddingTo = getContentTypeFromTypeKey(selectedItems[0].type)

                const items = selectedItems.map(selectedItem => {
                    return selectedItem.item ?? null
                }).filter(itemToCheck => !_.isNil(itemToCheck))

                // Because apparently, filtering out every null or undefined item before adding them to the container bears a massive risk of adding a 
                // null or undefined item according to TypeScript. Either I'm very bad at my job, or TypeScript is lying (but I don't know why)
                // @ts-ignore
                this.addItemsToContainer(items)
            }
        },
        async onMultipleSpecimensSelected(selectedSpecimens: SIMMItemSelected[]) {
            if(selectedSpecimens?.length) {
                this.currentlyAddingTo = ListContentType.SPECIMEN

                const specimens = selectedSpecimens.map(selectedItem => {
                    return selectedItem.specimen
                }).filter(specimenToCheck => !_.isNil(specimenToCheck))

                // Because apparently, filtering out every null or undefined specimen before adding them to the container bears a massive risk of adding a 
                // null or undefined specimen according to TypeScript. Either I'm very bad at my job, or TypeScript is lying (but I don't know why)
                // @ts-ignore
                this.addSpecimensToContainer(specimens)
            }
        },
        async addItemsToContainer(items: (Item | SearchResult)[], onSuccess?: (res) => void, onError?: (err) => void) {
            (this as any).$containerDataStore.addItems(this.container, items).subscribe(
                async res => {
                    await this.addItemsToResults(items)

                    this.currentlyAddingTo = null
                    if(onSuccess) {
                        onSuccess(res)
                    }
                },

                err => {
                    if(onError) {
                        onError(err)
                    } else {
                        this.failedItemsSelections = items.map(item => {
                            return {
                                item: item,
                                currentlyUploading: false,
                            } as ItemSelectionFailure
                        })
                        ;(this.$refs.selectItemsErrorDialog as InstanceType<typeof SelectItemsErrorDialog>).openModal()
                    }
                },
            )
        },
        async addSpecimensToContainer(specimens: SpecimenResource[], onSuccess?: (res) => void, onError?: (err) => void) {
            (this as any).$containerDataStore.addSpecimens(this.container, specimens).subscribe(
                async res => {
                    await this.addSpecimensToResults(specimens)

                    this.currentlyAddingTo = null
                    if(onSuccess) {
                        onSuccess(res)
                    }
                },

                err => {
                    if(onError) {
                        onError(err)
                    } else {
                        this.failedSpecimensSelections = specimens.map(specimen => {
                            return {
                                specimen: specimen,
                                currentlyUploading: false,
                            } as SpecimenSelectionFailure
                        })
                        ;(this.$refs.selectItemsErrorDialog as InstanceType<typeof SelectSpecimensErrorDialog>).openModal()
                    }
                },
            )
        },
        async addItemsToResults(itemsToAdd: (Item | SearchResult)[]) {
            const itemIds: string[] = itemsToAdd.map(item => {
                return item.id
            })

            if(itemIds.length > 1) {
                // if we have more than 1 item it is more efficient to get them all at once
                // but we have to wait, to let the reindexation update the location of items, before adding items to results
                await new Promise(resolve => setTimeout(resolve, 500))

                const itemsSearchResult: SearchResponse<SearchResult | Wemi> = await (this as any).$itemDataStore.searchAllOnline(
                    {
                        filteredIds: itemIds,
                        pageSize: MAX_NB_CHAIN_ADD + 1,
                    }
                ).pipe(
                    first(),
                ).toPromise()

                let items: (SearchResult | Item)[] = itemsToAdd
                if(itemsSearchResult?.results?.length) {
                    items = itemsSearchResult?.results as SearchResult[]
                } else if(itemsSearchResult?.allData?.length) {
                    items = itemsSearchResult.allData as SearchResult[]
                }

                for(const item of items) {
                    this.addItemToResults(item)
                }

            } else {
                // if we fetch the item directly, we don't have to wait for reindexation to be completed to get the updated location
                const item = await (this as any).$itemDataStore.fetchOneOnlineMode(itemIds[0]).pipe(
                    first(),
                    catchError(err => {
                        return of(itemsToAdd[0])
                    })
                ).toPromise()

                this.addItemToResults(item)
            }
        },
        addItemToResults(item: Item | SearchResult) {
            const type = item.entityType ?? item.type
            const wrapperType: ItemTypeWrapper['wrapperType'] = isSearchResult(item) ? 'SearchResult' : 'Item'

            if(type) {
                if(type.includes('Container')) {
                    this.subContainers.push({
                        item: item,
                        wrapperType: wrapperType,
                        itemType: 'Container',
                    })
                    this.currentlyAddingTo = null
                    return
                }
                else if(type.includes('Accessory')) {
                    this.accessories.push({
                        item: item,
                        wrapperType: wrapperType,
                        itemType: 'Accessory',
                    })
                    this.currentlyAddingTo = null
                    return
                }
            }

            this.items.push({
                item: item,
                wrapperType: wrapperType,
                itemType: 'Item',
            })
        },
        async addSpecimensToResults(specimensToAdd: SpecimenResource[]) {
            const specimenIds: string[] = specimensToAdd.map(specimen => {
                return specimen.id
            })

            if(specimenIds.length > 1) {
                // if we have more than 1 specimen it is more efficient to get them all at once
                // but we have to wait, to let the reindexation update the location of specimen, before adding specimen to results
                await new Promise(resolve => setTimeout(resolve, 500))

                const specimensSearchResult: SearchResponse<SpecimenResource> = await (this as any).$specimenDataStore.searchAllOnline(
                    {
                        filteredIds: specimenIds,
                        pageSize: MAX_NB_CHAIN_ADD + 1,
                    }
                ).pipe(
                    first(),
                ).toPromise()

                let specimens: SpecimenResource[] = specimensToAdd
                if(specimensSearchResult?.results?.length) {
                    specimens = specimensSearchResult?.results as SpecimenResource[]
                } else if(specimensSearchResult?.allData?.length) {
                    specimens = specimensSearchResult.allData as SpecimenResource[]
                }

                for(const specimen of specimens) {
                    this.addSpecimenToResults(specimen)
                }

            } else {
                // if we fetch the item directly, we don't have to wait for reindexation to be completed to get the updated location
                const specimen = await (this as any).$specimenDataStore.fetchOneOnlineMode(specimenIds[0]).pipe(
                    first(),
                    catchError(err => {
                        return of(specimensToAdd[0])
                    })
                ).toPromise()

                this.addSpecimenToResults(specimen)
            }
        },
        addSpecimenToResults(specimen: SpecimenResource) {
            this.specimens.push({
                item: specimen,
                wrapperType: 'Specimen',
                itemType: 'Specimen',
            })
        },
        onRetryAddItem(index: number) {
            if(0 <= index && index < this.failedItemsSelections.length) {
                const failedItem = this.failedItemsSelections[index]
                this.failedItemsSelections[index] = {...failedItem, currentlyUploading: true} as ItemSelectionFailure

                this.addItemsToContainer(
                    [failedItem.item],
                    successRes => {
                        this.failedItemsSelections.splice(index, 1)
                    },
                    err => {
                        this.failedItemsSelections[index] = {...failedItem, currentlyUploading: false} as ItemSelectionFailure
                    },
                )
            }
        },
        onCancelAddItem(index: number) {
            this.failedItemsSelections.splice(index, 1)
        },
        onRetryAddAllItems() {
            if(this.failedItemsSelections.length) {
                this.failedItemsSelections[0] = {...this.failedItemsSelections[0], currentlyUploading: true} as ItemSelectionFailure
            }
            this.addItemsToContainer(this.failedItemsSelections.map(
                failedItem => {
                    return failedItem.item
                }),
                successRes => {
                    (this.$refs.selectItemsErrorDialog as InstanceType<typeof SelectItemsErrorDialog>).closeModal()
                    this.failedItemsSelections = []
                },
            )
        },
        onCancelAddAllItems() {
            this.failedItemsSelections = []
            this.currentlyAddingTo = null
            ;(this.$refs.selectItemsErrorDialog as InstanceType<typeof SelectItemsErrorDialog>).closeModal()
        },
        onRetryAddSpecimen(index: number) {
            if(0 <= index && index < this.failedSpecimensSelections.length) {
                const failedSpecimen = this.failedSpecimensSelections[index]
                this.failedSpecimensSelections[index] = {...failedSpecimen, currentlyUploading: true} as SpecimenSelectionFailure

                this.addSpecimensToContainer(
                    [failedSpecimen.specimen],
                    successRes => {
                        this.failedSpecimensSelections.splice(index, 1)
                    },
                    err => {
                        this.failedSpecimensSelections[index] = {...failedSpecimen, currentlyUploading: false} as SpecimenSelectionFailure
                    },
                )
            }
        },
        onCancelAddSpecimen(index: number) {
            this.failedSpecimensSelections.splice(index, 1)
        },
        onRetryAddAllSpecimens() {
            if(this.failedSpecimensSelections.length) {
                this.failedSpecimensSelections[0] = {...this.failedSpecimensSelections[0], currentlyUploading: true} as SpecimenSelectionFailure
            }
            this.addSpecimensToContainer(this.failedSpecimensSelections.map(
                failedSpecimen => {
                    return failedSpecimen.specimen
                }),
                _ => {
                    (this.$refs.selectSpecimensErrorDialog as InstanceType<typeof SelectSpecimensErrorDialog>).closeModal()
                    this.failedSpecimensSelections = []
                },
            )
        },
        onCancelAddAllSpecimens() {
            this.failedSpecimensSelections = []
            this.currentlyAddingTo = null
            ;(this.$refs.selectSpecimensErrorDialog as InstanceType<typeof SelectSpecimensErrorDialog>).closeModal()
        },
        openAddItemModal() {
            (this.$refs.addItemModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },
        openAddSpecimenModal() {
            (this.$refs.addSpecimenModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },
        openAddAccessoryModal() {
            (this.$refs.addAccessoryModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },
        openAddContainerModal() {
            (this.$refs.addContainerModal as InstanceType<typeof SelectItemMultipleModal>).open()
        },
        toggleContainerOpen(open: boolean = true) {
            if (
                    (
                        this.container.containerItemSchema.openContainer
                        && (this.container.locationItemSchema?.permanentLocation || this.container.locationItemSchema?.realTimeLocation)
                    )
                    || !this.container.containerItemSchema.openContainer
                ) {
                this.container.containerItemSchema.openContainer = open
                ;(this as any).$containerDataStore.update(
                    this.container.id,
                    this.container,
                    API_ENDPOINTS[this.container.type],
                ).subscribe(() => {
                    const alertPayload = {
                        message: open ? this.$t('container.alerts.containerOpened') : this.$t('container.alerts.containerClosed'),
                        variant: "success"
                    }
                    this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                })
            }
        },
        onDelete(itemWrapper: ItemTypeWrapper) {
            let content$: Observable<Item | SpecimenResource>

            switch (itemWrapper.itemType) {
                case 'Specimen':
                    content$ = (this as any).$specimenDataStore.fetchOne((itemWrapper.item as SpecimenResource).id)
                    break
                default:
                    // if itemWrapper.item is a SearchResult we can't use it to update (so we fetch the item first)
                    content$ = (this as any).$itemDataStore.fetchOne((itemWrapper.item as any).id)
                    if(itemWrapper.wrapperType == 'Item') {
                        content$ = of(itemWrapper.item as Item)
                    }
                    break
            }

            content$.pipe(
                switchMap(content => {
                    switch (itemWrapper.itemType) {
                        case 'Specimen':
                            return (this as any).$containerDataStore.removeSpecimen(this.container?.id, content as SpecimenResource)
                        default:
                            return (this as any).$containerDataStore.removeItem(this.container?.id, content as Item)
                    }
                }),
                delay(1000),
            ).subscribe(() => {
                this.unsubscribeFrom('container')

                switch (itemWrapper.itemType) {
                    case 'Specimen':
                        this.unsubscribeFrom('specimens')
                        this.subscribeToEntity({ operationType: 'delete', contentType: 'specimen' })
                        break
                    default:
                        this.unsubscribeFrom('items')
                        this.subscribeToEntity({ operationType: 'delete', contentType: 'item' })
                        break
                }
            })
        },
        deleteContainer() {
            if(this.container.id) {
                (this as any).$containerDataStore.delete(this.container.id).subscribe(() => {
                    this.$router.push({ name: TrackingRoutes.CONTAINERS })
                })
            }
        },
        openLocationModal() {
            (this.$refs.chooseItemModal as InstanceType<typeof ChooseItemModal>).open()
        },
        getOneExpectedChangeForLocationUpdate(locationId: string): ExpectedChange | undefined {
            const childrens = this.items.concat(this.subContainers).concat(this.accessories)

            // since the subscribeToItems method returns SearchResult or Wemi, we only return an element in this format
            // In reality it only returns SearchResult so we only implement that one yet
            // NB: We are not able to check for a change on an element that is not a SearchResult as the path of updated element might differ
            let elementThatWillBeUpdated = childrens.find(item => {
                if(item.wrapperType === 'SearchResult') {
                    return searchResultSelectors.realTimeLocationId(item.item as SearchResult) !== locationId
                }

                return false
            })

            if(elementThatWillBeUpdated) {
                return {
                    operationType: 'update',
                    expectedValue: {
                        idItem: searchResultSelectors.id(elementThatWillBeUpdated.item as SearchResult),
                        path: searchResultSelectors.pathsStrings['realTimeLocationId'][0],
                        value: locationId,
                    },
                }
            }

            return undefined
        },
        addLocation({ location: { id: locationId }, setPermanentLocation }: { location: { id: string }, setPermanentLocation: boolean }) {
            let locationPath: string
            if (setPermanentLocation) {
                locationPath = 'locationItemSchema.permanentLocation.permanentLocation'
            } else {
                locationPath = 'locationItemSchema.realTimeLocation.realTimeLocation'
            }
            _.set(this.container, locationPath, locationId)


            const expectedChange = this.getOneExpectedChangeForLocationUpdate(locationId)

            ;(this as any).$containerDataStore.update(this.container.id, this.container).pipe(
                switchMap(container => {
                    return (this as any).$containerDataStore.cascadeLocationOnContent(this.container.id)
                })
            )
            .subscribe(() => {
                this.unsubscribeFrom('container')
                this.unsubscribeFrom('items')
                if(expectedChange) {
                    this.subscribeToEntity(expectedChange)
                } else {
                    this.subscribeToEntity()
                }
            })
        },
    },
    computed: {
        itemIds() {
            return this.items.filter(identity)
                .map(wrapper => {
                    if(wrapper.wrapperType === 'Wemi') {
                        return (wrapper.item as Wemi).item?.id
                    }
                    return (wrapper.item as SearchResult | Item ).id
                })
        },
        specimenIds() {
            return this.specimens.filter(identity)
                .map(wrapper => {
                    if(wrapper.wrapperType === 'Specimen') {
                        return (wrapper.item as SpecimenResource).id
                    }
                    return (wrapper.item as SearchResult | SpecimenResource ).id
                })
        },
        containerIds() {
            return this.subContainers.filter(identity)
                .map(wrapper => {
                    if(wrapper.wrapperType === 'Wemi') {
                        return (wrapper.item as Wemi).item?.id
                    }
                    return (wrapper.item as SearchResult | Container ).id
                })
        },
        accessoryIds() {
            return this.accessories.filter(identity)
                .map(wrapper => {
                    if(wrapper.wrapperType === 'Wemi') {
                        return (wrapper.item as Wemi).item?.id
                    }
                    return (wrapper.item as SearchResult | Item ).id
                })
        },
    },
})
